// Simple client-safe error logging utility
// This version doesn't import any server-only modules

interface ErrorLog {
  message: string;
  error?: Error;
  context?: Record<string, unknown>;
  traceId?: string;
  requestId?: string;
  source?: string;
  resource?: string;
  method?: string;
  errorCode?: number;
}

interface UserAgentBrand {
  brand?: string;
  version?: string;
}

interface UserAgentData {
  brands?: readonly UserAgentBrand[];
  mobile?: boolean;
  platform?: string;
}

interface NavigatorWithUserAgentData extends Navigator {
  userAgentData?: UserAgentData;
}

interface ClientEnvironment {
  browser?: string;
  browserLanguage?: string;
  isMobile?: boolean;
  pagePath?: string;
  platform?: string;
  referrer?: string;
  screenResolution?: string;
  url?: string;
  userAgent?: string;
  viewport?: string;
}

// Safely check if we're in development mode
const isDevelopmentMode = () => {
  try {
    return typeof process !== 'undefined' && process?.env?.NODE_ENV === 'development';
  } catch {
    return false; // Default to production behavior
  }
};

const shouldLogInDevelopment = () => {
  try {
    return typeof process !== 'undefined' && process?.env?.NEXT_PUBLIC_LOG_ERRORS_IN_DEV === 'true';
  } catch {
    return false;
  }
};

function getCallerInfo(): string {
  const error = new Error();
  const stack = error.stack?.split('\n');
  const callerLine = stack?.[3] ?? '';
  const match = callerLine.match(/\((.+?)\)/);
  const fullPath = match?.[1];
  if (!fullPath) return 'unknown';

  const srcPath = fullPath.split('/src/')[1];
  return srcPath?.replace(/\.[^/.]+$/, '') ?? 'unknown';
}

const CLIENT_DEDUPE_WINDOW_MS = 60_000;
const recentClientErrorSignatures = new Map<string, number>();

function generateTraceId(): string {
  const randomUuid = globalThis.crypto?.randomUUID?.();
  if (randomUuid) return randomUuid;

  return `trace-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function resolveStack(message: string, error?: Error): string | undefined {
  if (error?.stack) {
    return error.stack;
  }

  return new Error(message).stack;
}

function resolveSource(source: string | undefined): string {
  const normalized = source?.trim();
  if (normalized) {
    return normalized;
  }

  return getCallerInfo();
}

function resolveMethod(method: string | undefined): string | undefined {
  const normalized = method?.trim();
  return normalized ? normalized.toUpperCase() : undefined;
}

function resolveErrorCode(errorCode: number | undefined): number | undefined {
  if (errorCode == null || !Number.isInteger(errorCode)) {
    return undefined;
  }

  return errorCode;
}

function shouldIgnoreClientError(error: Error | undefined): boolean {
  if (!error) {
    return false;
  }

  if (error.name === 'AbortError') {
    return true;
  }

  return false;
}

function normalizeText(value: string | null | undefined): string | undefined {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

function resolveDimension(value: number | undefined): string | undefined {
  if (value == null || !Number.isFinite(value) || value <= 0) {
    return undefined;
  }

  return Math.round(value).toString();
}

function resolveBrowserName(
  userAgent: string | undefined,
  brands: readonly UserAgentBrand[] | undefined,
): string | undefined {
  const preferredBrand = brands?.find((brand) => {
    const normalizedBrand = normalizeText(brand.brand);
    return normalizedBrand != null && normalizedBrand !== 'Not_A Brand';
  });

  if (preferredBrand?.brand) {
    return preferredBrand.brand;
  }

  if (!userAgent) {
    return undefined;
  }

  if (/edg\//i.test(userAgent)) {
    return 'Microsoft Edge';
  }

  if (/opr\//i.test(userAgent) || /opera/i.test(userAgent)) {
    return 'Opera';
  }

  if (/firefox\//i.test(userAgent) || /fxios\//i.test(userAgent)) {
    return 'Firefox';
  }

  if (/chrome\//i.test(userAgent) || /crios\//i.test(userAgent)) {
    return 'Chrome';
  }

  if (/safari\//i.test(userAgent)) {
    return 'Safari';
  }

  return undefined;
}

function resolveMobileState(
  userAgent: string | undefined,
  userAgentData: UserAgentData | undefined,
): boolean | undefined {
  if (typeof userAgentData?.mobile === 'boolean') {
    return userAgentData.mobile;
  }

  if (!userAgent) {
    return undefined;
  }

  return /android|iphone|ipad|ipod|iemobile|blackberry|mobile/i.test(userAgent);
}

function collectClientEnvironment(): ClientEnvironment | undefined {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return undefined;
  }

  const userAgentData = (navigator as NavigatorWithUserAgentData).userAgentData;
  const userAgent = normalizeText(navigator.userAgent);
  const browser = resolveBrowserName(userAgent, userAgentData?.brands);
  const browserLanguage = normalizeText(navigator.language);
  const screenWidth = resolveDimension(window.screen?.width);
  const screenHeight = resolveDimension(window.screen?.height);
  const viewportWidth = resolveDimension(window.innerWidth);
  const viewportHeight = resolveDimension(window.innerHeight);
  const platform = normalizeText(userAgentData?.platform) ?? normalizeText(navigator.platform);
  const referrer = normalizeText(document.referrer);
  const pagePath = normalizeText(window.location?.pathname);
  const url = normalizeText(window.location?.href);
  const isMobile = resolveMobileState(userAgent, userAgentData);

  const environment: ClientEnvironment = {
    browser,
    browserLanguage,
    isMobile,
    pagePath,
    platform,
    referrer,
    screenResolution: screenWidth && screenHeight ? `${screenWidth}x${screenHeight}` : undefined,
    url,
    userAgent,
    viewport: viewportWidth && viewportHeight ? `${viewportWidth}x${viewportHeight}` : undefined,
  };

  const hasValue = Object.values(environment).some((value) => value !== undefined);
  return hasValue ? environment : undefined;
}

function buildClientErrorSignature(input: {
  source: string;
  resource?: string;
  method?: string;
  errorCode?: number;
  message: string;
}): string {
  return [
    input.source,
    input.resource ?? 'null',
    input.method ?? 'null',
    input.errorCode ?? 'null',
    input.message,
  ].join('|');
}

function isDuplicateClientError(signature: string): boolean {
  const now = Date.now();
  const previousTimestamp = recentClientErrorSignatures.get(signature);

  if (previousTimestamp != null && now - previousTimestamp < CLIENT_DEDUPE_WINDOW_MS) {
    return true;
  }

  recentClientErrorSignatures.set(signature, now);

  // Best effort cleanup of stale entries.
  for (const [key, timestamp] of recentClientErrorSignatures.entries()) {
    if (now - timestamp > CLIENT_DEDUPE_WINDOW_MS) {
      recentClientErrorSignatures.delete(key);
    }
  }

  return false;
}

async function logError({
  message,
  error,
  context,
  traceId,
  requestId,
  source,
  resource,
  method,
  errorCode,
}: ErrorLog): Promise<void> {
  if (shouldIgnoreClientError(error)) {
    return;
  }

  const isDevelopment = isDevelopmentMode();
  const resolvedTraceId = traceId ?? generateTraceId();
  const resolvedStack = resolveStack(message, error);
  const resolvedErrorType = error?.name ?? 'Error';
  const resolvedSource = resolveSource(source);
  const resolvedMethod = resolveMethod(method);
  const resolvedErrorCode = resolveErrorCode(errorCode);
  const normalizedResource = resource?.trim();
  const resolvedResource = normalizedResource ? normalizedResource : null;
  const clientEnvironment = collectClientEnvironment();

  const signature = buildClientErrorSignature({
    source: resolvedSource,
    resource,
    method: resolvedMethod,
    errorCode: resolvedErrorCode,
    message,
  });

  if (isDuplicateClientError(signature)) {
    return;
  }

  // Always log to console
  console.error(`[${resolvedTraceId}] ERROR:`, message);
  if (error) {
    console.error(error);
  }
  if (context) {
    console.error('Context:', context);
  }

  // In development, we don't need to do anything else
  if (isDevelopment && !shouldLogInDevelopment()) {
    return;
  }

  // In production, send errors to our API endpoint
  try {
    // Send error to API endpoint
    await fetch('/api/log-error', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        stack: resolvedStack,
        errorType: resolvedErrorType,
        source: resolvedSource,
        resource: resolvedResource,
        method: resolvedMethod ?? null,
        errorCode: resolvedErrorCode ?? null,
        context,
        clientEnvironment,
        traceId: resolvedTraceId,
        requestId: requestId ?? null,
      }),
    });
  } catch (loggingError) {
    // Fail silently in production
    console.error('Failed to log error:', loggingError);
  }
}

export { logError };
